// src/context/SocialContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
    collection, query, orderBy, limit, onSnapshot, getDocs,
    addDoc, updateDoc, deleteDoc, serverTimestamp,
    doc, runTransaction, getDoc, setDoc
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useUserData } from './UserContext';
import { getPaths } from '../firebase.paths';
import { globalAppId } from '../utils/constants';
import { isUserOnVacation } from '../utils/userUtils';

const SocialContext = createContext();
export const useSocialData = () => useContext(SocialContext);

const MAX_SUBJECT_LEN = 120;
const MAX_TEXT_LEN    = 1000;

export const SocialProvider = ({ children }) => {
    const { db, auth, userProfile } = useAuth();
    const { refreshLeaderboard } = useUserData();
    const paths = useMemo(() => getPaths(globalAppId), []);

    const [newsList, setNewsList] = useState([]);
    const [suggestionsList, setSuggestionsList] = useState([]);
    const [coinsExpirationDate, setCoinsExpirationDate] = useState('');

    // Notícias — onSnapshot (limite 20, muda com frequência: vale manter tempo real)
    useEffect(() => {
        if (!db) return;
        const qNews = query(collection(db, paths.news), orderBy('createdAt', 'desc'), limit(20));
        const unsubNews = onSnapshot(
            qNews,
            (snap) => setNewsList(snap.docs.map(d => ({
                id: d.id, ...d.data(),
                createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date()
            }))),
            (err) => console.error('[SocialContext] Erro ao buscar news:', err.code, err.message)
        );
        return () => unsubNews();
    }, [db, paths]);

    // Sugestões — getDocs pontual (muda raramente; evita 200 leituras por sessão em tempo real)
    const refreshSuggestions = useCallback(async () => {
        if (!db) return;
        try {
            const qSugg = query(collection(db, paths.suggestions), orderBy('createdAt', 'desc'), limit(200));
            const snap  = await getDocs(qSugg);
            setSuggestionsList(snap.docs.map(d => ({
                id: d.id, ...d.data(),
                createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date()
            })));
        } catch (err) {
            console.error('[SocialContext] Erro ao buscar suggestions:', err.code, err.message);
        }
    }, [db, paths]);

    useEffect(() => {
        if (db) refreshSuggestions();
    }, [db]); // eslint-disable-line react-hooks/exhaustive-deps -- refreshSuggestions é estável, db é a dep real

    // Carrega data de expiração de RPCoins salva no Firestore
    useEffect(() => {
        if (!db) return;
        getDoc(doc(db, paths.config, 'generalSettings'))
            .then(snap => {
                if (snap.exists() && snap.data().coinsExpirationDate)
                    setCoinsExpirationDate(snap.data().coinsExpirationDate);
            })
            .catch(() => {}); // silencioso: config opcional
    }, [db, paths]);

    // ── Notícias ──────────────────────────────────────────────────────────────
    const handlePublishNews = async (subject, body, authorName) =>
        addDoc(collection(db, paths.news), { subject, body, authorName, createdAt: serverTimestamp() });

    const handleEditNews = async (id, subject, body) =>
        updateDoc(doc(db, paths.news, id), { subject, body });

    const handleDeleteNews = async (id) =>
        deleteDoc(doc(db, paths.news, id));

    // ── Sugestões ─────────────────────────────────────────────────────────────
    const handleSubmitSuggestion = async (text, subject, anonymous = false) => {
        if (!auth.currentUser) throw new Error("Login necessário.");
        if (isUserOnVacation(userProfile)) throw new Error("Ação bloqueada: Você está em período de férias.");

        const cleanSubject = subject.trim();
        const cleanText    = text.trim();

        if (!cleanSubject) throw new Error("O assunto não pode estar vazio.");
        if (!cleanText)    throw new Error("O texto não pode estar vazio.");
        if (cleanSubject.length > MAX_SUBJECT_LEN)
            throw new Error(`Assunto muito longo (máx. ${MAX_SUBJECT_LEN} caracteres).`);
        if (cleanText.length > MAX_TEXT_LEN)
            throw new Error(`Texto muito longo (máx. ${MAX_TEXT_LEN} caracteres).`);

        await addDoc(collection(db, paths.suggestions), {
            text:       cleanText,
            subject:    cleanSubject,
            authorId:   auth.currentUser.uid,
            authorName: anonymous ? 'Anônimo' : userProfile.name,
            anonymous,
            status:     'pending',
            influence:  2,
            createdAt:  serverTimestamp(),
        });
        await refreshSuggestions();
    };

    const handleApproveSuggestion = async (id, rating, pointsAwarded) => {
        await runTransaction(db, async (transaction) => {
            const suggRef  = doc(db, paths.suggestions, id);
            const suggSnap = await transaction.get(suggRef);
            if (!suggSnap.exists()) throw new Error("Sugestão não encontrada.");
            if (suggSnap.data().status === 'approved')
                throw new Error("Esta sugestão já foi aprovada anteriormente.");

            const authorId  = suggSnap.data().authorId;
            const userRef   = doc(db, paths.users, authorId);
            const userSnap  = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("Autor não encontrado.");

            const currentPoints  = userSnap.data().points  || 0;
            const currentCredits = userSnap.data().credits || 0;

            transaction.update(userRef, {
                points:  currentPoints  + pointsAwarded,
                credits: currentCredits + pointsAwarded,
            });
            transaction.update(suggRef, {
                status:        'approved',
                influence:     rating,
                pointsAwarded: pointsAwarded,
                approvedAt:    serverTimestamp(),
            });

            const transRef = doc(collection(db, paths.transactions));
            transaction.set(transRef, {
                targetUserId:  authorId,
                targetUserName: userSnap.data().name,
                adminName:     userProfile?.name || 'Admin',
                pointsChange:  pointsAwarded,
                creditsChange: pointsAwarded,
                reason:        `Sugestão Aprovada (${rating} Estrelas)`,
                type:          'suggestion_reward',
                status:        'approved',
                createdAt:     serverTimestamp(),
            });
        });
        await refreshSuggestions();
        await refreshLeaderboard(); // autor recebeu pontos
    };

    // Protegido: não pode rejeitar sugestão já aprovada
    const handleRejectSuggestion = async (id, reason) => {
        await runTransaction(db, async (transaction) => {
            const suggRef  = doc(db, paths.suggestions, id);
            const suggSnap = await transaction.get(suggRef);
            if (!suggSnap.exists()) throw new Error("Sugestão não encontrada.");
            if (suggSnap.data().status === 'approved')
                throw new Error("Não é possível rejeitar uma sugestão já aprovada.");
            transaction.update(suggRef, {
                status:          'rejected',
                rejectionReason: reason?.trim() || '',
                rejectedAt:      serverTimestamp(),
            });
        });
        await refreshSuggestions();
    };

    const handleEditSuggestion = async (id, data) => {
        await updateDoc(doc(db, paths.suggestions, id), data);
        await refreshSuggestions();
    };

    const handleDeleteSuggestion = async (id) => {
        await deleteDoc(doc(db, paths.suggestions, id));
        await refreshSuggestions();
    };

    // Edita status/estrelas de uma sugestão já processada e ajusta pontos do autor atomicamente
    const handleEditSuggestionHistory = async (id, { status, stars, currentPoints, currentStatus, authorId }) => {
        const newPoints = status === 'approved' ? (stars === 3 ? 350 : stars === 2 ? 200 : 100) : 0;
        const diff      = newPoints - (currentStatus === 'approved' ? (currentPoints || 0) : 0);

        await runTransaction(db, async (transaction) => {
            const suggRef  = doc(db, paths.suggestions, id);
            const userRef  = doc(db, paths.users, authorId);
            const userSnap = await transaction.get(userRef);

            if (userSnap.exists()) {
                transaction.update(userRef, {
                    points:  (userSnap.data().points  || 0) + diff,
                    credits: (userSnap.data().credits || 0) + diff,
                });
            }
            transaction.update(suggRef, {
                status,
                influence:     status === 'approved' ? stars : null,
                pointsAwarded: newPoints,
            });
        });
        await refreshSuggestions();
        if (diff !== 0) await refreshLeaderboard(); // pontos mudaram
    };

    // Salva a data de expiração de RPCoins e publica aviso automático no mural
    const handleSetCoinsExpiration = async (date) => {
        if (!date) throw new Error("Informe uma data válida.");
        const configRef = doc(db, paths.config, 'generalSettings');
        await setDoc(configRef, { coinsExpirationDate: date }, { merge: true });
        setCoinsExpirationDate(date);

        const [year, month, day] = date.split('-');
        const formattedDate = `${day}/${month}/${year}`;
        const subject = '⏳ Atenção: RPCoins com prazo de expiração';
        const body =
            `Aviso importante sobre seu saldo de RPCoins!\n\n` +
            `Os RPCoins acumulados expirarão em ${formattedDate}. ` +
            `Certifique-se de resgatar seus prêmios na Lojinha antes desta data.\n\n` +
            `Fique atento ao seu saldo e aproveite os benefícios antes do prazo!`;
        await handlePublishNews(subject, body, userProfile?.name || 'RH');
    };

    const value = {
        newsList, suggestionsList,
        coinsExpirationDate,
        refreshSuggestions,
        handlePublishNews, handleEditNews, handleDeleteNews,
        handleSubmitSuggestion, handleApproveSuggestion,
        handleRejectSuggestion, handleDeleteSuggestion, handleEditSuggestion,
        handleEditSuggestionHistory,
        handleSetCoinsExpiration,
        MAX_SUBJECT_LEN, MAX_TEXT_LEN,
    };

    return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
};
